import type { Direction, GameSettings, GameState, Point } from './types'

const vectors: Record<Direction, Point> = {
  up: { x: 0, y: -1 },
  down: { x: 0, y: 1 },
  left: { x: -1, y: 0 },
  right: { x: 1, y: 0 },
}

const opposite: Record<Direction, Direction> = {
  up: 'down', down: 'up', left: 'right', right: 'left',
}

const pointKey = ({ x, y }: Point) => `${x}:${y}`

function random(seed: number): [number, number] {
  let next = seed | 0
  next ^= next << 13
  next ^= next >>> 17
  next ^= next << 5
  return [next >>> 0, (next >>> 0) / 4294967296]
}

function pickFree(seed: number, settings: GameSettings, blocked: Set<string>): [number, Point | null] {
  const free: Point[] = []
  for (let y = 0; y < settings.rules.rows; y += 1) {
    for (let x = 0; x < settings.rules.columns; x += 1) {
      if (!blocked.has(`${x}:${y}`)) free.push({ x, y })
    }
  }
  if (!free.length) return [seed, null]
  const [nextSeed, value] = random(seed)
  return [nextSeed, free[Math.floor(value * free.length)]]
}

function createObstacles(settings: GameSettings, snake: Point[], seed: number): [number, Point[]] {
  const safe = new Set<string>()
  snake.forEach((point) => safe.add(pointKey(point)))
  const head = snake[0]
  for (let y = head.y - 3; y <= head.y + 3; y += 1) {
    for (let x = head.x - 5; x <= head.x + 5; x += 1) safe.add(`${x}:${y}`)
  }
  const total = Math.floor(settings.rules.columns * settings.rules.rows * settings.rules.obstaclePercent / 100)
  const obstacles: Point[] = []
  let currentSeed = seed
  for (let index = 0; index < total; index += 1) {
    const blocked = new Set([...safe, ...obstacles.map(pointKey)])
    const [nextSeed, point] = pickFree(currentSeed, settings, blocked)
    currentSeed = nextSeed
    if (!point) break
    obstacles.push(point)
  }
  return [currentSeed, obstacles]
}

function fillFoods(state: GameState, settings: GameSettings): GameState {
  const foods = [...state.foods]
  let seed = state.seed
  while (foods.length < settings.rules.foodCount) {
    const blocked = new Set([...state.snake, ...state.obstacles, ...foods].map(pointKey))
    const [nextSeed, point] = pickFree(seed, settings, blocked)
    seed = nextSeed
    if (!point) break
    foods.push(point)
  }
  return { ...state, foods, seed }
}

export function createGame(settings: GameSettings, seed = Date.now() >>> 0): GameState {
  const centerX = Math.floor(settings.rules.columns / 2)
  const centerY = Math.floor(settings.rules.rows / 2)
  const snake = Array.from({ length: 4 }, (_, index) => ({ x: centerX - index, y: centerY }))
  const [nextSeed, obstacles] = createObstacles(settings, snake, seed || 1)
  return fillFoods({
    snake,
    direction: 'right',
    queuedDirections: [],
    foods: [],
    obstacles,
    status: 'ready',
    score: 0,
    foodEaten: 0,
    level: 1,
    elapsedMs: 0,
    growPending: 0,
    seed: nextSeed,
    tick: 0,
    event: 'idle',
    eventSeq: 0,
  }, settings)
}

export function startGame(state: GameState): GameState {
  if (state.status !== 'ready') return state
  return { ...state, status: 'playing', event: 'start', eventSeq: state.eventSeq + 1 }
}

export function queueDirection(state: GameState, direction: Direction): GameState {
  if (state.status === 'gameover' || state.status === 'won') return state
  const baseDirection = state.queuedDirections.at(-1) ?? state.direction
  if (direction === baseDirection || direction === opposite[baseDirection]) return state
  const queuedDirections = [...state.queuedDirections, direction].slice(-2)
  return {
    ...state,
    queuedDirections,
    status: state.status === 'ready' ? 'playing' : state.status,
    event: state.status === 'ready' ? 'start' : state.event,
    eventSeq: state.status === 'ready' ? state.eventSeq + 1 : state.eventSeq,
  }
}

export function togglePause(state: GameState): GameState {
  if (state.status === 'playing') {
    return { ...state, status: 'paused', event: 'pause', eventSeq: state.eventSeq + 1 }
  }
  if (state.status === 'paused') {
    return { ...state, status: 'playing', event: 'resume', eventSeq: state.eventSeq + 1 }
  }
  return state
}

export function getSpeed(state: GameState, settings: GameSettings): number {
  return Math.min(24, settings.rules.startSpeed + (state.level - 1) * settings.rules.speedStep)
}

export function stepGame(state: GameState, settings: GameSettings): GameState {
  if (state.status !== 'playing') return state
  const direction = state.queuedDirections[0] ?? state.direction
  const queuedDirections = state.queuedDirections.slice(1)
  const vector = vectors[direction]
  let head = { x: state.snake[0].x + vector.x, y: state.snake[0].y + vector.y }

  if (settings.rules.wallMode === 'wrap') {
    head = {
      x: (head.x + settings.rules.columns) % settings.rules.columns,
      y: (head.y + settings.rules.rows) % settings.rules.rows,
    }
  }

  const wallHit = head.x < 0 || head.y < 0 || head.x >= settings.rules.columns || head.y >= settings.rules.rows
  const obstacleHit = state.obstacles.some((point) => point.x === head.x && point.y === head.y)
  const eatenIndex = state.foods.findIndex((point) => point.x === head.x && point.y === head.y)
  const isEating = eatenIndex >= 0
  const keepsTail = isEating || state.growPending > 0
  const bodyToCheck = keepsTail ? state.snake : state.snake.slice(0, -1)
  const selfHit = settings.rules.selfCollision && bodyToCheck.some((point) => point.x === head.x && point.y === head.y)

  if (wallHit || obstacleHit || selfHit) {
    return { ...state, direction, queuedDirections: [], status: 'gameover', event: 'gameover', eventSeq: state.eventSeq + 1 }
  }

  const snake = [head, ...state.snake]
  let growPending = state.growPending
  const foods = [...state.foods]
  let score = state.score
  let foodEaten = state.foodEaten
  let level = state.level

  if (isEating) {
    foods.splice(eatenIndex, 1)
    score += 100 * state.level
    foodEaten += 1
    level = 1 + Math.floor(foodEaten / 5)
    growPending += settings.rules.growth - 1
  } else if (growPending > 0) {
    growPending -= 1
  } else {
    snake.pop()
  }

  let next = fillFoods({
    ...state,
    snake,
    direction,
    queuedDirections,
    foods,
    score,
    foodEaten,
    level,
    growPending,
    elapsedMs: state.elapsedMs + 1000 / getSpeed(state, settings),
    tick: state.tick + 1,
    event: isEating ? 'eat' : 'move',
    eventSeq: isEating ? state.eventSeq + 1 : state.eventSeq,
  }, settings)

  if (next.foods.length === 0) {
    next = { ...next, status: 'won', event: 'win', eventSeq: next.eventSeq + 1 }
  }
  return next
}
